import React from "react"
import { useChain } from "@azuro-org/sdk"
import { openModal } from "@locmod/modal"
import { useChainId, useSwitchChain } from "wagmi"
import { type Chain } from "viem"
import { arbitrum, base } from "viem/chains"
import cx from "classnames"

import { Icon } from "@/components/ui"
import { NODE_ENV } from "@/config/env"
import { useTokenSelection } from "@/contexts/TokenSelectionContext"
import useChainProviderHook, {
  setChainProviderOverride,
} from "@/hooks/useChainProvider"
import useDebouncedRouterPush from "@/SX/hooks/useDebounceRouterPush"
import { constants } from "helpers"
import { config } from "wallet"

import ChainButton from "./ChainButton"
import ChainCurrency from "./ChainCurrency"
import {
  buildFilteredChains,
  type ChainConfig,
  getIsActive,
  resolveCurrentChainConfig,
} from "./helper"

const ChainSelect: React.FC<{ close?: () => void; isAAWallet: boolean }> = ({
  close,
  isAAWallet,
}) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const { setAppChainId } = useChain()
  const { switchChain } = useSwitchChain()

  const chainId = useChainId()
  const { selectedTokenIndex, setSelectedTokenIndex } = useTokenSelection()
  const { isOvertimeChain, isSXChain, provider } = useChainProviderHook()

  // console.log({ isOvertimeChain, isSXChain })
  // Memoize the filtered chains to prevent unnecessary re-renders
  const filteredChains = React.useMemo(
    () => buildFilteredChains(isAAWallet, config.chains, NODE_ENV as string),
    [isAAWallet, config.chains]
  )

  const applyProviderOverride = (chain: Chain & ChainConfig) => {
    if (chain.id === arbitrum.id) {
      if (chain.provider === "sx") {
        setChainProviderOverride(chain.id, "sx", false, false, true)
      } else if (chain.provider === "overtime") {
        setChainProviderOverride(chain.id, "overtime", true, false, false)
      }
    }
  }

  const applyBaseSelection = (chain: Chain & ChainConfig) => {
    if (chain.id === base.id) {
      const newTokenIndex = chain.currency === "USDC" ? 0 : 5
      setSelectedTokenIndex(newTokenIndex)
      if (chain.currency === "WETH") {
        setAppChainId(chain.id as any)
      }
    }
  }

  const isSameSupportedProvider = (
    currentChainId: number,
    newChain: Chain & ChainConfig
  ) => {
    // Get current chain provider
    const newProvider = newChain.provider

    // If both chains are from the same provider, return true
    if (provider === newProvider) {
      return true
    }

    // Special case for Base chain - check if both are from the same provider based on currency
    if (currentChainId === base.id && newChain.id === base.id) {
      const currentCurrency = selectedTokenIndex === 0 ? "USDC" : "WETH"
      const currentProviderByCurrency =
        currentCurrency === "USDC" ? "overtime" : "azuro"
      return currentProviderByCurrency === newProvider
    }

    // Special case for Arbitrum chain - check if both are from the same provider
    if (currentChainId === arbitrum.id && newChain.id === arbitrum.id) {
      const currentProviderByState = isSXChain
        ? "sx"
        : isOvertimeChain
          ? "overtime"
          : null
      return currentProviderByState === newProvider
    }

    return false
  }

  const debouncedRouterPush = useDebouncedRouterPush()
  const handleChainChange = React.useCallback(
    async (chain: Chain & ChainConfig) => {
      const { id: chainId } = chain

      try {
        // Apply provider override BEFORE switching chains
        applyProviderOverride(chain)
        applyBaseSelection(chain)

        switchChain({ chainId: chainId as any })

        if (
          !constants.SX_CHAIN_IDS.has(chainId) &&
          !constants.OVERTIME_CHAIN_IDS.has(chainId)
        ) {
          setAppChainId(chainId as any)
        }

        setIsOpen(false)
        close?.()

        // Only call debouncedRouterPush if switching to a different supported provider
        if (!isSameSupportedProvider(chainId, chain)) {
          debouncedRouterPush("/")
        }
      } catch (error) {
        openModal("SwitchNetworkModal", {
          chainId: chainId as any,
        })
      }
    },
    [
      switchChain,
      setAppChainId,
      close,
      setSelectedTokenIndex,
      isSXChain,
      isOvertimeChain,
      selectedTokenIndex,
      debouncedRouterPush,
    ]
  )

  const currentChainConfig = React.useMemo(() => {
    const chainConfigs = isAAWallet
      ? constants.SOCIAL_LOGIN_CHAIN_CURRENCY_CONFIGS
      : constants.CUSTOM_CHAIN_CURRENCY_CONFIGS

    return resolveCurrentChainConfig(
      chainConfigs,
      chainId,
      selectedTokenIndex,
      isSXChain,
      isOvertimeChain
    )
  }, [chainId, selectedTokenIndex, isAAWallet, isSXChain, isOvertimeChain])

  const isArbitrumSX =
    chainId === arbitrum.id &&
    currentChainConfig?.currency === "USDC" &&
    currentChainConfig?.provider === "sx"

  return (
    <div className=" p-1 rounded-md">
      <button
        className="p-2 flex items-center justify-between w-full group/select"
        disabled={isAAWallet === undefined}
        onClick={() => isAAWallet !== undefined && setIsOpen(!isOpen)}
      >
        <div className="flex items-center">
          <ChainCurrency
            className="mr-2"
            chainClassName="border-accent-grey-850"
            chainId={chainId}
            size={4}
            currencyIcon={currentChainConfig?.betTokenIcon}
          />
          <div className="text-caption-13">
            {currentChainConfig?.name} {currentChainConfig?.currency}{" "}
            {isArbitrumSX && "SX"}
          </div>
        </div>
        {isAAWallet === undefined ? (
          <div className="size-4 animate-spin rounded-full border-2 border-accent-grey-700 border-t-accent-grey-200" />
        ) : (
          <Icon
            className={cx(
              "size-4 text-accent-grey-200 group-hover/select:text-accent-grey-30 transition-colors",
              {
                "rotate-180": isOpen,
              }
            )}
            name="interface/chevron_down"
          />
        )}
      </button>
      {isOpen && (
        <div className="w-full space-y-0.5">
          {filteredChains.map((chain: Chain & ChainConfig) => {
            const isActive = getIsActive(
              chain,
              chainId,
              selectedTokenIndex,
              isSXChain,
              isOvertimeChain
            )

            return (
              <ChainButton
                key={`${chain.id}-${chain.currency}-${chain.provider}`}
                chain={chain}
                isActive={isActive}
                onClick={() => {
                  handleChainChange(chain)
                }}
              />
            )
          })}
        </div>
      )}
    </div>
  )
}

export default ChainSelect
